import React, { useState, useEffect } from 'react';
import apiService from '../../services/api'; // Import your API service

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Check for saved credentials on component mount
  useEffect(() => {
    // Use sessionStorage instead of localStorage for compatibility
    const savedCredentials = sessionStorage.getItem('rememberedCredentials');
    if (savedCredentials) {
      try {
        const { username, password } = JSON.parse(savedCredentials);
        setFormData({ username, password });
        setRememberMe(true);
      } catch (e) {
        console.error('Error parsing saved credentials:', e);
      }
    }
  }, []);

  // Floating animation for balloons
  useEffect(() => {
    const balloons = document.querySelectorAll('.balloon');
    balloons.forEach((balloon, index) => {
      balloon.style.animationDelay = `${index * 2}s`;
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use your existing API service
      const result = await apiService.login({
        username: formData.username,
        password: formData.password
      });

      if (result.success) {
        // Handle remember me functionality
        if (rememberMe) {
          sessionStorage.setItem('rememberedCredentials', JSON.stringify({
            username: formData.username,
            password: formData.password
          }));
        } else {
          sessionStorage.removeItem('rememberedCredentials');
        }
        
        // Successful login - prepare user data
        const userData = result.data;
        
        const loginSuccessData = {
          type: 'login-success',
          data: {
            username: userData.username || formData.username,
            token: userData.token || userData.access_token,
            name: userData.name || userData.full_name || userData.first_name || 'User',
            userId: userData.user_id || userData.id,
            ...userData // Include any other user data from API
          }
        };

        // Send message to WinForms application
        try {
          if (window.chrome && window.chrome.webview && window.chrome.webview.postMessage) {
            console.log('Sending login success to WinForms...', loginSuccessData);
            window.chrome.webview.postMessage(loginSuccessData);
          } else {
            console.log('Chrome WebView not available, creating fallback...');
            // Fallback: try to call WinForms callback directly
            if (window.winformsCallback && window.winformsCallback.handleLoginSuccess) {
              window.winformsCallback.handleLoginSuccess(JSON.stringify(loginSuccessData));
            } else {
              console.log('WinForms callback not available');
            }
          }
        } catch (commError) {
          console.error('Error communicating with WinForms:', commError);
        }

        // Also call the original onLogin if provided (for web-only scenarios)
        if (onLogin && typeof onLogin === 'function') {
          onLogin(loginSuccessData.data);
        }

      } else {
        // API returned an error
        setError(result.error || 'Invalid username or password. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen fixed inset-0 overflow-hidden bg-gradient-to-b from-purple-900 via-purple-800 to-purple-900">
      {/* Mountain Background */}
      <div className="absolute inset-0 w-full h-full">
        {/* Stars */}
        <div className="absolute inset-0 w-full h-full">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full opacity-60"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        {/* Clouds */}
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <div className="absolute top-10 left-10 w-32 h-16 bg-gradient-to-r from-blue-200/30 to-purple-200/30 rounded-full blur-sm"></div>
          <div className="absolute top-20 right-20 w-24 h-12 bg-gradient-to-r from-blue-200/20 to-purple-200/20 rounded-full blur-sm"></div>
          <div className="absolute top-32 left-1/3 w-28 h-14 bg-gradient-to-r from-blue-200/25 to-purple-200/25 rounded-full blur-sm"></div>
        </div>

        {/* Hot Air Balloons */}
        <div className="absolute inset-0 w-full h-full">
          <div className="balloon absolute top-16 left-20 w-12 h-16">
            <div className="w-12 h-12 bg-gradient-to-b from-yellow-400 to-orange-500 rounded-full"></div>
            <div className="w-0.5 h-4 bg-yellow-600 mx-auto"></div>
            <div className="w-3 h-2 bg-yellow-700 mx-auto rounded-sm"></div>
          </div>
          
          <div className="balloon absolute top-24 right-32 w-10 h-14">
            <div className="w-10 h-10 bg-gradient-to-b from-pink-400 to-purple-500 rounded-full"></div>
            <div className="w-0.5 h-3 bg-purple-600 mx-auto"></div>
            <div className="w-2 h-1.5 bg-purple-700 mx-auto rounded-sm"></div>
          </div>
          
          <div className="balloon absolute top-20 left-1/2 w-8 h-12">
            <div className="w-8 h-8 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-full"></div>
            <div className="w-0.5 h-3 bg-indigo-600 mx-auto"></div>
            <div className="w-2 h-1 bg-indigo-700 mx-auto rounded-sm"></div>
          </div>
        </div>

        {/* Mountains */}
        <div className="absolute bottom-0 w-full h-2/3">
          {/* Back mountains */}
          <div className="absolute bottom-0 left-0 w-full h-full">
            <div className="absolute bottom-0 left-0 w-1/3 h-4/5 bg-gradient-to-t from-purple-900 to-purple-700 transform origin-bottom-left skew-x-12"></div>
            <div className="absolute bottom-0 left-1/4 w-1/3 h-full bg-gradient-to-t from-purple-800 to-purple-600 transform origin-bottom-left -skew-x-6"></div>
            <div className="absolute bottom-0 right-0 w-1/3 h-3/4 bg-gradient-to-t from-purple-900 to-purple-700 transform origin-bottom-right -skew-x-12"></div>
          </div>
          
          {/* Front mountains */}
          <div className="absolute bottom-0 w-full h-full">
            <div className="absolute bottom-0 left-10 w-1/4 h-3/5 bg-gradient-to-t from-slate-800 to-slate-700 transform origin-bottom-left skew-x-6"></div>
            <div className="absolute bottom-0 right-10 w-1/4 h-2/3 bg-gradient-to-t from-slate-800 to-slate-700 transform origin-bottom-right -skew-x-6"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center h-screen w-screen">
        <div className="w-full max-w-md mx-4">
          {/* Login Card */}
          <div className="backdrop-blur-xl bg-slate-800/90 rounded-2xl shadow-2xl border border-slate-600/30 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-6 text-center border-b border-slate-600/30">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-1">USER SIGNIN</h2>
              <p className="text-sm text-slate-400">Purchasing System</p>
            </div>

            {/* Form Section */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="USERNAME"
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="PASSWORD"
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center text-slate-300 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded border-2 transition-all ${rememberMe ? 'bg-blue-500 border-blue-500' : 'border-slate-500'}`}>
                        {rememberMe && (
                          <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="ml-2">Keep me logged in</span>
                  </label>
                </div>
                
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Signing In...
                    </div>
                  ) : (
                    'LOGIN'
                  )}
                </button>
              </form>
            </div>
          </div>
          
          <div className="text-center mt-6 text-slate-300 text-xs">
            © SmartProbe Purchasing System All rights reserved.
          </div>
        </div>
      </div>

      <style jsx>{`
        .balloon {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
};

export default Login;